import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Alert, CircularProgress, Tabs, Tab
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import ModuleHub from '../components/ModuleHub';
import { getFinancialReports, getAgingReport } from '../../../services/api';

const FinancialReportsPage = () => {
  const location = useLocation();
  const initialTab = location.pathname.includes('profit-loss') ? 3
    : location.pathname.includes('balance-sheet') ? 4
    : location.pathname.includes('aging') ? 2
    : location.pathname.includes('trial-balance') ? 0 : 0;
  const [tab, setTab] = useState(initialTab);
  const [financial, setFinancial] = useState(null);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (tab === 2) { const r = await getAgingReport(); setAging(r.data); }
      else { const r = await getFinancialReports(); setFinancial(r.data); }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const fmt = (v) => parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

  return (
    <ModuleHub moduleId="reporting" showFeatures={false}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Trial Balance" />
        <Tab label="Summary" />
        <Tab label="AP Aging" />
        <Tab label="Profit & Loss" />
        <Tab label="Balance Sheet" />
      </Tabs>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <>
          {tab === 0 && financial && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Account</TableCell><TableCell>Type</TableCell><TableCell align="right">Balance</TableCell></TableRow></TableHead>
                <TableBody>
                  {(financial.trialBalance || []).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell align="right">{row.formatted || fmt(row.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {tab === 1 && financial?.summary && (
            <Grid container spacing={2}>
              {[
                { label: 'Total Assets', value: financial.summary.totalAssets },
                { label: 'Total Liabilities', value: financial.summary.totalLiabilities },
                { label: 'Total Equity', value: financial.summary.totalEquity },
                { label: 'Total Income', value: financial.summary.totalIncome },
                { label: 'Total Expenses', value: financial.summary.totalExpenses },
                { label: 'Net Income', value: financial.summary.netIncome }
              ].map(item => (
                <Grid item xs={12} sm={6} md={4} key={item.label}>
                  <Card><CardContent>
                    <Typography color="text.secondary">{item.label}</Typography>
                    <Typography variant="h5" fontWeight={700}>{fmt(item.value)}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>
          )}
          {tab === 2 && aging && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Current', value: aging.current },
                  { label: '1-30 Days', value: aging.days30 },
                  { label: '31-60 Days', value: aging.days60 },
                  { label: '90+ Days', value: aging.days90plus }
                ].map(item => (
                  <Grid item xs={6} md={3} key={item.label}>
                    <Card><CardContent>
                      <Typography color="text.secondary">{item.label}</Typography>
                      <Typography variant="h6" fontWeight={700}>{fmt(item.value)}</Typography>
                    </CardContent></Card>
                  </Grid>
                ))}
              </Grid>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Bill</TableCell><TableCell>Vendor</TableCell><TableCell>Due</TableCell><TableCell align="right">Outstanding</TableCell><TableCell>Days Overdue</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(aging.items || []).map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.bill_number}</TableCell>
                        <TableCell>{item.vendor}</TableCell>
                        <TableCell>{item.due_date}</TableCell>
                        <TableCell align="right">{fmt(item.outstanding)}</TableCell>
                        <TableCell>{item.days_overdue}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {tab === 3 && financial?.summary && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TableContainer component={Paper}><Table size="small">
                  <TableHead><TableRow><TableCell colSpan={2}><strong>Income</strong></TableCell></TableRow></TableHead>
                  <TableBody>
                    <TableRow><TableCell>Total Income</TableCell><TableCell align="right">{fmt(financial.summary.totalIncome)}</TableCell></TableRow>
                    <TableRow><TableCell>Total Expenses</TableCell><TableCell align="right">{fmt(financial.summary.totalExpenses)}</TableCell></TableRow>
                    <TableRow><TableCell><strong>Net Income</strong></TableCell><TableCell align="right"><strong>{fmt(financial.summary.netIncome)}</strong></TableCell></TableRow>
                  </TableBody>
                </Table></TableContainer>
              </Grid>
            </Grid>
          )}
          {tab === 4 && financial?.summary && (
            <Grid container spacing={2}>
              {[
                { label: 'Total Assets', value: financial.summary.totalAssets },
                { label: 'Total Liabilities', value: financial.summary.totalLiabilities },
                { label: 'Total Equity', value: financial.summary.totalEquity },
                { label: 'Net Income (YTD)', value: financial.summary.netIncome }
              ].map(item => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                  <Card><CardContent>
                    <Typography color="text.secondary">{item.label}</Typography>
                    <Typography variant="h6" fontWeight={700}>{fmt(item.value)}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </ModuleHub>
  );
};

export default FinancialReportsPage;
